import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Search, User, LogOut, Menu, X, Phone, Mail, Truck, Star, Heart } from "lucide-react";
import { API, LOGO_URL } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const Header = () => {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catMenuOpen, setCatMenuOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [activeSubcats, setActiveSubcats] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/categories`).then(res => setCategories(res.data || [])).catch(() => {});
  }, []);

  // Debounced live-search against the products API. Closes when the
  // query is too short or the user explicitly dismisses the dropdown.
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }
    setSearchLoading(true);
    const id = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/products`, { params: { search: q, limit: 8 } });
        setSearchResults(res.data || []);
        setSearchOpen(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 220);
    return () => clearTimeout(id);
  }, [searchQuery]);

  // Close dropdown on outside click or Esc.
  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('[data-search-root]')) setSearchOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("click", onClick);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, []);

  // Close hamburger drawer on Esc
  useEffect(() => {
    if (!catMenuOpen) return;
    const onEsc = (e) => { if (e.key === "Escape") { setCatMenuOpen(false); setActiveCat(null); } };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [catMenuOpen]);

  const openCategory = async (catName) => {
    setActiveCat(catName);
    setActiveSubcats([]);
    try {
      const res = await axios.get(`${API}/categories/${encodeURIComponent(catName)}/subcategories`);
      setActiveSubcats(res.data || []);
    } catch {
      setActiveSubcats([]);
    }
  };

  const closeCatMenu = () => { setCatMenuOpen(false); setActiveCat(null); setActiveSubcats([]); };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const cartItemCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <header className="header sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-[#4c6272] text-white text-sm py-2">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Phone className="w-4 h-4" /> 020 3633 8047</span>
            <span className="hidden sm:flex items-center gap-1"><Mail className="w-4 h-4" /> orders@carepointuk.net</span>
            <Link to="/about" className="hidden md:inline text-white/90 hover:text-white underline-offset-2 hover:underline" data-testid="header-about-link">About Us</Link>
          </div>
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            <span>Free delivery on orders over &pound;30</span>
            <span className="hidden lg:inline-flex items-center gap-1 ml-4 pl-4 border-l border-white/30">
              <Star className="w-3 h-3 fill-[#00b67a] text-[#00b67a]" />
              <Star className="w-3 h-3 fill-[#00b67a] text-[#00b67a]" />
              <Star className="w-3 h-3 fill-[#00b67a] text-[#00b67a]" />
              <Star className="w-3 h-3 fill-[#00b67a] text-[#00b67a]" />
              <Star className="w-3 h-3 fill-[#00b67a] text-[#00b67a]" />
              <span className="text-xs ml-1">Trustpilot 100k+ reviews</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={LOGO_URL} alt="Carepoint Health Supplies" className="h-24 md:h-36 lg:h-44 w-auto" loading="eager" decoding="async" />
          </Link>

          {/* Search bar - desktop */}
          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8 relative" data-search-root>
            <div className="search-bar w-full">
              <input
                type="text"
                placeholder="Search products by name, brand or SKU..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => { if (searchResults.length) setSearchOpen(true); }}
                data-testid="search-input"
              />
              <button type="submit" data-testid="search-button">
                <Search className="w-5 h-5" />
              </button>
            </div>

            {searchOpen && searchQuery.trim().length >= 2 && (
              <div
                className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-xl z-50 max-h-[28rem] overflow-y-auto"
                data-testid="live-search-dropdown"
              >
                {searchLoading && (
                  <div className="p-3 text-sm text-gray-400">Searching…</div>
                )}
                {!searchLoading && searchResults.length === 0 && (
                  <div className="p-3 text-sm text-gray-400">No products found for "{searchQuery}"</div>
                )}
                {!searchLoading && searchResults.length > 0 && (
                  <>
                    {searchResults.map((p) => (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => pickResult(p.id)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-teal-50 border-b last:border-b-0"
                        data-testid={`live-search-result-${p.id}`}
                      >
                        <img
                          src={p.image_url || "https://via.placeholder.com/40"}
                          alt=""
                          className="w-10 h-10 object-contain bg-gray-50 rounded flex-shrink-0"
                          loading="lazy"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.sku} · {p.category}</div>
                        </div>
                        <div className="text-sm font-semibold text-[#00a99d] whitespace-nowrap">
                          &pound;{Number(p.price).toFixed(2)}
                        </div>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => { setSearchOpen(false); navigate(`/products?search=${encodeURIComponent(searchQuery)}`); }}
                      className="w-full px-3 py-2 text-center text-sm text-[#00a99d] font-medium hover:bg-teal-50 sticky bottom-0 bg-white border-t"
                      data-testid="live-search-see-all"
                    >
                      See all results for "{searchQuery}" →
                    </button>
                  </>
                )}
              </div>
            )}
          </form>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Link to="/account" className="hidden sm:flex items-center gap-2 text-gray-700 hover:text-[#00a99d]" data-testid="account-link">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium">{user.name.split(' ')[0]}</span>
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin" className="hidden sm:block text-sm font-medium text-[#00a99d] hover:underline" data-testid="admin-link">
                    Admin
                  </Link>
                )}
                <button onClick={logout} className="hidden sm:flex items-center gap-1 text-gray-500 hover:text-red-600" data-testid="logout-button">
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <Link to="/login" className="hidden sm:flex items-center gap-2 text-gray-700 hover:text-[#00a99d]" data-testid="login-link">
                <User className="w-5 h-5" />
                <span className="text-sm font-medium">Sign In</span>
              </Link>
            )}

            <Link to="/wishlist" className="relative hidden sm:flex items-center gap-2 text-gray-700 hover:text-rose-500" data-testid="wishlist-link" title="Saved items">
              <Heart className="w-6 h-6" />
            </Link>

            <Link to="/cart" className="relative flex items-center gap-2 text-gray-700 hover:text-[#00a99d]" data-testid="cart-link">
              <ShoppingCart className="w-6 h-6" />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 badge badge-primary" data-testid="cart-count">
                  {cartItemCount}
                </span>
              )}
            </Link>

            <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="menu-toggle">
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <form onSubmit={handleSearch} className="md:hidden mt-4">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit">
              <Search className="w-5 h-5" />
            </button>
          </div>
        </form>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden mt-4 py-4 border-t">
            <nav className="flex flex-col gap-4">
              <Link to="/products" className="text-gray-700 hover:text-[#00a99d] font-medium" onClick={() => setMenuOpen(false)}>All Products</Link>
              {categories.map(cat => (
                <Link key={cat.name} to={`/products?category=${encodeURIComponent(cat.name)}`} className="text-gray-700 hover:text-[#00a99d]" onClick={() => setMenuOpen(false)}>{cat.name}</Link>
              ))}
              {user ? (
                <>
                  <Link to="/account" className="text-gray-700 hover:text-[#00a99d]" onClick={() => setMenuOpen(false)}>My Account</Link>
                  <Link to="/wishlist" className="text-gray-700 hover:text-rose-500" onClick={() => setMenuOpen(false)}>Saved Items</Link>
                  {user.role === "admin" && <Link to="/admin" className="text-[#00a99d]" onClick={() => setMenuOpen(false)}>Admin Panel</Link>}
                  <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left text-red-600">Sign Out</button>
                </>
              ) : (
                <Link to="/login" className="text-gray-700 hover:text-[#00a99d]" onClick={() => setMenuOpen(false)}>Sign In</Link>
              )}
            </nav>
          </div>
        )}
      </div>

      {/* Category navigation - All Products hamburger + scrollable tabs */}
      <nav className="category-nav hidden md:block relative">
        <div className="max-w-7xl mx-auto px-4 flex gap-1 overflow-x-auto whitespace-nowrap scrollbar-hide" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          <button
            type="button"
            onClick={() => setCatMenuOpen(!catMenuOpen)}
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#00a99d] text-white px-4 py-2 hover:bg-[#008c83] font-semibold"
            data-testid="all-products-hamburger"
          >
            <Menu className="w-4 h-4" />
            All Products
          </button>
          {categories.slice(0, 22).map(cat => (
            <Link key={cat.name} to={`/products?category=${encodeURIComponent(cat.name)}`} className="flex-shrink-0">
              {cat.name} <span className="text-xs opacity-60">({cat.count})</span>
            </Link>
          ))}
        </div>

        {/* Mega-menu dropdown */}
        {catMenuOpen && (
          <>
            <div className="fixed inset-0 bg-black/30 z-40" onClick={closeCatMenu} data-testid="cat-menu-backdrop" />
            <div className="absolute left-0 right-0 top-full bg-white shadow-2xl border-t-2 border-[#00a99d] z-50 max-h-[70vh] overflow-y-auto" data-testid="cat-menu-panel">
              <div className="max-w-7xl mx-auto grid grid-cols-12 gap-0">
                {/* Categories list */}
                <div className="col-span-4 border-r border-gray-200 py-3 max-h-[60vh] overflow-y-auto">
                  {categories.map(cat => (
                    <button
                      key={cat.name}
                      onMouseEnter={() => openCategory(cat.name)}
                      onClick={() => openCategory(cat.name)}
                      className={`w-full text-left px-5 py-2.5 flex items-center justify-between hover:bg-gray-50 transition ${activeCat === cat.name ? "bg-[#e6f7f5] text-[#00a99d] font-semibold" : "text-gray-800"}`}
                      data-testid={`cat-menu-cat-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span>{cat.name}</span>
                      <span className="text-xs text-gray-400">{cat.count}</span>
                    </button>
                  ))}
                </div>

                {/* Subcategories panel */}
                <div className="col-span-8 py-3 px-6 max-h-[60vh] overflow-y-auto">
                  {!activeCat ? (
                    <div className="text-gray-500 text-sm flex items-center justify-center h-32">
                      Hover or click a category to see its sub-sections
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-lg text-[#00a99d]">{activeCat}</h3>
                        <Link
                          to={`/products?category=${encodeURIComponent(activeCat)}`}
                          onClick={closeCatMenu}
                          className="text-sm text-[#00a99d] hover:underline"
                          data-testid="cat-menu-view-all-cat"
                        >
                          View all {activeCat} &rarr;
                        </Link>
                      </div>
                      {activeSubcats.length === 0 ? (
                        <div className="text-gray-500 text-sm">No subcategories yet</div>
                      ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                          {activeSubcats.map(sub => (
                            <Link
                              key={sub.name}
                              to={`/products?category=${encodeURIComponent(activeCat)}&subcategory=${encodeURIComponent(sub.name)}`}
                              onClick={closeCatMenu}
                              className="px-3 py-2 rounded text-sm text-gray-700 hover:bg-[#e6f7f5] hover:text-[#00a99d] transition flex items-center justify-between"
                              data-testid={`cat-menu-sub-${sub.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                            >
                              <span>{sub.name}</span>
                              <span className="text-xs text-gray-400 ml-2">{sub.count}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </nav>
    </header>
  );
};
